import "../spinner";
import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { StateMixin } from "../../mixins/state";
import { Routing } from "../../mixins/routing";
import { shared } from "../../styles";
import "../scroller";
import "../date-input";
import "../popover";
import "../issue";
import "../sortable-list";
import { app } from "../../init";
import "./dashboard-tile";
import "./dashboard-tile-vacation";
import { DASHBOARD_TILES, type DashboardTile, Permission } from "@pentacode/core";
import { Checkbox } from "../checkbox";
import "./dashboard-tile-sick-leave";
import "./dashboard-tile-birthdays";
import "./dashboard-tile-expiring-contracts";
import "./dashboard-tile-notes";

type Entries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

const tilesWithRequiredPermissions: Record<DashboardTile, Permission | "none"> = {
    birthdays: "manage.employees.core_data",
    sickLeaveRequests: "manage.employees.absences",
    vacationRequests: "manage.employees.absences",
    expiringContracts: "manage.employees.contract",
    notes: "none",
};

export function hasDashboardTilePermission(tile: DashboardTile) {
    const requiredPermission = tilesWithRequiredPermissions[tile];
    if (requiredPermission === "none") {
        return true;
    }
    return app.hasPermission(requiredPermission);
}

@customElement("ptc-dashboard")
export class Dashboard extends Routing(StateMixin(LitElement)) {
    routePattern = /^dashboard/;

    get routeTitle() {
        return "Dashboard";
    }

    get helpPage() {
        return "/handbuch";
    }

    @state()
    private _showBetaTile: boolean = false;

    static styles = [
        shared,
        Checkbox.styles,
        css`
            .dashboard {
                position: relative;
                display: grid;
                min-height: 100%;
                grid-template-columns: auto;
                grid-template-rows: min-content auto;
                grid-template-areas:
                    "controls"
                    "tiles";
            }

            .tile-controls {
                position: absolute;
                place-self: start end;
                margin: 1rem;
                grid-area: controls;
            }

            .tile-controls form {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                justify-content: start;
            }

            .tile-controls form label {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                padding: 0;
            }

            .dashboard-tiles {
                --tile-base-size: 15rem;
                --tile-gap: 1rem;

                z-index: 2;
                padding: 1rem;
                gap: var(--tile-gap);
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(var(--tile-base-size), 1fr));
                grid-auto-rows: min-content;
                grid-auto-flow: dense;
                grid-area: tiles;
            }

            .dashboard-header {
                display: flex;
                justify-content: space-between;
                gap: 0.5rem;
                padding: 1.5rem 1rem 0;
            }

            .dashboard-header h1 {
                font-size: 1.5rem;
                font-weight: 600;
                margin: 0;
                background: var(--gradient-brand);
                -webkit-text-fill-color: transparent;
                background-clip: text;
            }

            .dashboard-tiles.filler {
                overflow: hidden;
                position: absolute;
                inset: 0;
                pointer-events: none;
                z-index: 1;

                &::after {
                    content: "";
                    position: absolute;
                    inset: 0;
                    top: auto;
                    height: 15rem;
                    background: linear-gradient(to bottom, transparent, var(--color-bg-light));
                }
            }

            .beta-tile {
                padding: 1rem;
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                background: var(--gradient-purple);
                color: var(--color-bg);
                height: var(--tile-base-size);
            }

            .beta-tile h2 {
                font-size: 1.2rem;
                font-weight: 600;
                margin: 0;
            }
        `,
    ];

    render() {
        // We need to do this during render as the company is not available on every rerender
        if (app.company) {
            app.settings.dashboardTiles = app.settings.dashboardTiles.filter((tile) =>
                hasDashboardTilePermission(tile)
            );
        }
        return html`<div class="dashboard">
            <header class="dashboard-header">
                <h1>Hallo ${app.account?.firstName || "Benutzer"} – Willkommen im Pentacode Cockpit!</h1>
                <button class="slim transparent layout" title="Dashboard Einstellungen">
                    <i class="toggle-on large"></i>
                </button>
                <ptc-popover trigger="click">
                    <form
                        @change=${(ev: InputEvent) => {
                            const form = ev.currentTarget as HTMLFormElement;
                            const data = new FormData(form);

                            const selectedTiles = data.getAll("tiles") as DashboardTile[];
                            app.updateSettings({ dashboardTiles: selectedTiles }).catch(console.error);
                        }}
                    >
                        <fieldset>
                            <button
                                type="button"
                                @click=${() => {
                                    app.updateSettings({ dashboardTiles: [] }).catch(console.error);
                                    this.requestUpdate();
                                }}
                                class="transparent slim"
                            >
                                <i class="toggle-off"></i> Keine
                            </button>
                            <button
                                type="button"
                                @click=${() => {
                                    app.updateSettings({
                                        dashboardTiles: Object.keys(DASHBOARD_TILES)
                                            .filter((key) => hasDashboardTilePermission(key as DashboardTile))
                                            .map((key) => key as DashboardTile),
                                    }).catch(console.error);
                                    this.requestUpdate();
                                }}
                                class="transparent slim"
                            >
                                <i class="toggle-on"></i> Alle
                            </button>
                            ${(Object.entries(DASHBOARD_TILES) as Entries<typeof DASHBOARD_TILES>).map(
                                ([value, label]) => html`
                                    <ptc-checkbox-button
                                        name="tiles"
                                        buttonClass="transparent slim"
                                        .label=${label}
                                        .value=${value}
                                        ?checked=${app.settings.dashboardTiles.includes(value as DashboardTile)}
                                        ?disabled=${!hasDashboardTilePermission(value as DashboardTile)}
                                    ></ptc-checkbox-button>
                                `
                            )}
                        </fieldset>
                    </form>
                </ptc-popover>
            </header>
            <div class="dashboard-tiles">
                ${app.settings.dashboardTiles.includes("birthdays")
                    ? html`<ptc-dashboard-tile-birthdays></ptc-dashboard-tile-birthdays>`
                    : nothing}
                ${app.settings.dashboardTiles.includes("sickLeaveRequests")
                    ? html`<ptc-dashboard-tile-sick-leave></ptc-dashboard-tile-sick-leave>`
                    : nothing}
                ${app.settings.dashboardTiles.includes("vacationRequests")
                    ? html`<ptc-dashboard-tile-vacation></ptc-dashboard-tile-vacation>`
                    : nothing}
                ${app.settings.dashboardTiles.includes("expiringContracts")
                    ? html`<ptc-dashboard-tile-expiring-contracts></ptc-dashboard-tile-expiring-contracts>`
                    : nothing}
                ${app.settings.dashboardTiles.includes("notes")
                    ? html`<ptc-dashboard-tile-notes></ptc-dashboard-tile-notes>`
                    : nothing}
                ${this._showBetaTile
                    ? html`<ptc-dashboard-tile .size=${1}>
                          <div class="beta-tile">
                              <h2>Danke für deine Teilnahme am Pilot - Programm!</h2>
                              <p>Wir freuen uns über deine Rückmeldung und Vorschläge!</p>
                          </div>
                      </ptc-dashboard-tile> `
                    : nothing}
            </div>
            <div class="dashboard-tiles filler">
                <ptc-dashboard-tile
                    .size=${{ inline: 1, block: 2 }}
                    ?hidden=${!app.settings.dashboardTiles.includes("birthdays")}
                ></ptc-dashboard-tile>
                <ptc-dashboard-tile
                    .size=${{ inline: 1, block: 2 }}
                    ?hidden=${!app.settings.dashboardTiles.includes("sickLeaveRequests")}
                ></ptc-dashboard-tile>
                <ptc-dashboard-tile
                    .size=${{ inline: 1, block: 2 }}
                    ?hidden=${!app.settings.dashboardTiles.includes("vacationRequests")}
                ></ptc-dashboard-tile>
                <ptc-dashboard-tile
                    .size=${{ inline: 1, block: 2 }}
                    ?hidden=${!app.settings.dashboardTiles.includes("expiringContracts")}
                ></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}
                    ><span class="place-self-center text-shade-5">mehr folgt bald…</span></ptc-dashboard-tile
                >
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${2}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
                <ptc-dashboard-tile .size=${1}></ptc-dashboard-tile>
            </div>
        </div> `;
    }
}
